import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Icon } from '@mdi/react';
import { Variables } from '../base/variables';

const Cell = css`
    padding: 10px;
    line-height: normal;
    font-weight: normal;
    position: relative;
    transition: box-shadow 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: inset 0 0 0 0 transparent;
    color: ${Variables.Colors.n50};
    flex-shrink: 0;

    position: relative;

    :last-child {
        border-right: 0;
    }

    .resizer {
        display: inline-block;
        background-color: ${Variables.Colors.n50};
        width: 2px;
        padding: 0 5px;
        height: 100%;
        position: absolute;
        right: 5px;
        top: 0;
        transform: translateX(50%);
        z-index: 1;
        background-clip: content-box;

        touch-action: none;

        &.isResizing {
            background-color: ${Variables.Colors.r50};
            background-clip: content-box;
        }
    }
`;

export const TableHead = styled.div`
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: ${Variables.Colors.ghostWhite};
    user-select: none;
    text-align: left;
`;

const TableRowHover = css`
    cursor: pointer;
    display: inline-flex !important;

    &:hover {
        background-color: ${Variables.Colors.n5};
    }
`;

export const TableRow = styled.div<{ hoverable?: boolean; selected?: boolean }>`
    ${props => (!!props.hoverable ? TableRowHover : undefined)}
    ${props => (!!props.selected ? `
        background-color: ${Variables.Colors.n5} !important;
        display: inline-flex !important;
    ` : undefined)}
    position: relative;
    isolation: isolate;
    
    &:hover .row-actions {
        opacity: 1;
        pointer-events: auto;
    }
`;

export const TableSummaryRow = styled.div`
    background-color: ${Variables.Colors.n15_48};
    font-weight: 500;
`;

export const TableCell = styled.div`
    ${Cell}
    align-items: center;
    display: flex;
    overflow: hidden;
    white-space: nowrap;
`;

export const TableSummaryCell = styled.div`
    ${Cell}
    align-items: center;
    display: flex;
    overflow: hidden;
    white-space: nowrap;
    font-weight: 500;
`;

type TableHeaderProps = {
    canSort?: boolean;
    isSorted?: boolean;
    maxWidth?: number;
    minWidth?: number;
    width?: number | string;
};

export const TableHeader = styled.div<TableHeaderProps>`
    ${Cell}
    border-bottom: 1px solid ${Variables.Colors.n5};
    border-right: 1px solid ${Variables.Colors.n5};
    
    ${({ isSorted }) =>
        isSorted &&
        `
        color: ${Variables.Colors.n70};
        font-weight: 500;
    `};

    &:hover {
        color: ${({ canSort }) => canSort && Variables.Colors.p300};
    }
`;

export const Table = styled.div`
    display: block;
    width: 100%;
    min-width: fit-content;
`;

export const TableContainer = styled.div`
    position: relative;
    width: 100%;
    overflow: auto;
    box-shadow: 0 -0.5px 0 0 ${Variables.Colors.n15};
`;

export const TableBody = styled.div`
    ${TableRow} {
        box-shadow: 0 -0.5px 0 0 ${Variables.Colors.n15};
        max-width: unset !important;
        min-width: 100% !important;
    }
    ${TableCell} {
        align-items: center;
        letter-spacing: 0.2px;
        color: ${Variables.Colors.n70};
    }
`;

export const TableWrapper = styled.section`
    position: relative;
`;

export const ControlsPanel = styled.section`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding: 16px 16px;
    width: 100%;
`;

export const ActiveFiltersPanel = styled.section`
    &:not(:empty) {
        padding: 24px;
        padding-bottom: 0;
    }
`;

export const SortIcon = styled(Icon)`
    display: inline-block;
    font-weight: 900;
    position: relative;
    margin-left: 9px;
    vertical-align: middle;
    svg {
        fill: ${Variables.Colors.p75};
    }
`;

export const NavigationContainer = styled.section`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    width: 100%;
    flex: 1;
    &:empty {
        padding: 0;
    }
`;

export const EmptyContainer = styled.section`
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const EmptyTitle = styled.section`
    font-size: 18px;
`;

export const SubjectCell = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
`;
